import { isCapacitorPlatform } from '@/utils/platform';
import { APP_VERSION } from '@/version';

const SUPABASE_URL =
  (import.meta as any).env?.VITE_SUPABASE_URL ||
  (typeof process !== 'undefined'
    ? process.env.VITE_SUPABASE_URL ||
      process.env.NEXT_PUBLIC_SUPABASE_URL ||
      process.env.SUPABASE_URL
    : undefined);

const DEFAULT_VERSION_URL = SUPABASE_URL
  ? `${SUPABASE_URL}/storage/v1/object/public/versions/version.json`
  : 'https://gxnmcjgxredhctobhkki.supabase.co/storage/v1/object/public/versions/version.json';

export interface VersionInfo {
  version: string;
  mandatory: boolean;
  changelog?: string;
  link?: string;
  released?: boolean;
  availableAt?: string;
}

export function getCurrentTrack(): string {
  return (
    (import.meta as any).env?.VITE_APP_TRACK ||
    (typeof process !== 'undefined' ? process.env.VITE_APP_TRACK : undefined) ||
    'stable'
  );
}

/**
 * Restituisce l'URL assoluto del file version.json utilizzato per
 * verificare la disponibilità di nuove versioni dell'app.
 * @returns URL del file version.json remoto
 */
export function getVersionJsonUrl(): string {
  return (
    (import.meta as any).env?.VITE_APP_VERSION_URL ||
    (typeof process !== 'undefined' ? process.env.VITE_APP_VERSION_URL : undefined) ||
    DEFAULT_VERSION_URL
  );
}

export async function fetchVersionInfo(
  track: string
): Promise<VersionInfo | null> {
  const url = getVersionJsonUrl();

  try {
    const res = await fetch(url, { cache: 'no-cache' });
    if (!res.ok) return null;
    const data = await res.json();
    const info = data.tracks?.[track] || data;
    if (!info.version) return null;
    if (info.released === false) return null;
    if (info.availableAt && new Date(info.availableAt) > new Date()) return null;
    return {
      version: info.version,
      mandatory: Boolean(info.mandatory),
      changelog: info.changelog || undefined,
      link: info.link || undefined,
      released: info.released !== undefined ? Boolean(info.released) : undefined,
      availableAt: info.availableAt || undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Deprecated: use fetchVersionInfo instead.
 * Kept for backward compatibility.
 */
export async function fetchLatestVersion(track = 'stable'): Promise<string | null> {
  const info = await fetchVersionInfo(track);
  return info?.version ?? null;
}

const STORE_URLS = {
  alpha:
    (import.meta as any).env?.VITE_APP_STORE_ALPHA_URL ||
    (typeof process !== 'undefined'
      ? process.env.VITE_APP_STORE_ALPHA_URL
      : undefined),
  beta:
    (import.meta as any).env?.VITE_APP_STORE_BETA_URL ||
    (typeof process !== 'undefined'
      ? process.env.VITE_APP_STORE_BETA_URL
      : undefined),
  candidate:
    (import.meta as any).env?.VITE_APP_STORE_CANDIDATE_URL ||
    (typeof process !== 'undefined'
      ? process.env.VITE_APP_STORE_CANDIDATE_URL
      : undefined) ||
    'https://play.google.com/apps/internaltest/4701716998596287619',
  stable:
    (import.meta as any).env?.VITE_APP_STORE_STABLE_URL ||
    (typeof process !== 'undefined'
      ? process.env.VITE_APP_STORE_STABLE_URL
      : undefined) ||
    'https://play.google.com/store/apps/details?id=app.lovable.spesasemplice',
};

function getStoreUrl(track: string): string {
  return (
    STORE_URLS[track] ||
    STORE_URLS.stable ||
    'https://play.google.com/store/apps/details?id=app.lovable.spesasemplice'
  );
}

export async function openStorePage(url?: string): Promise<void> {
  const storeUrl = url || getStoreUrl(getCurrentTrack());

  if (isCapacitorPlatform()) {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: storeUrl });
      return;
    } catch {
      // fall back to window.open
    }
  }
  window.open(storeUrl, '_blank');
}
